// @flow strict

import React, { useRef, useLayoutEffect, type Node } from 'react';
import ReactDOM from 'react-dom';

import * as styles from './modal.module.less';

/**
 * Type of component properties
 */
type ModalProps = {
  className: string,
  onClose: () => void,
  children: Node,
};

/**
 * Prevent background from scrolling while modal is visible.
 *
 * @returns {number}
 */
function lockBodyScroll(): number {
  const body = document.body;
  if (document.documentElement == null || body == null) {
    return 0;
  }

  // get current scroll position, since position change of body
  // will reset scroll position.
  const scrollTop = document.documentElement.scrollTop || body.scrollTop;

  // change body style to fixed, makes it unable to scroll
  // set inverted top position, to show background at same position
  body.classList.add('omq-modal-open');
  body.style.top = `-${scrollTop}px`;

  // return scrollTop for later use
  return scrollTop;
}

/**
 * Revert changes made by lockBodyScroll.
 *
 * @param {number} scrollTop - Position to restore body scroll
 */
function unlockBodyScroll(scrollTop: number): void {
  const body = document.body;
  if (body == null) {
    return;
  }

  // restore body style
  body.classList.remove('omq-modal-open');
  body.style.top = '0px';

  // restore scroll position
  body.scrollTop = scrollTop;

  if (document.documentElement != null) {
    document.documentElement.scrollTop = scrollTop;
  }
}

/**
 * Create container for modal component.
 *
 * @returns {HTMLDivElement}
 */
function createModalContainer(): HTMLElement {
  const container = document.createElement('div');
  container.classList.add(styles.modalWrapper);

  return container;
}

/**
 * Hook to prepare and remove modal container.
 *
 * @returns {HTMLElement}
 */
function useModalContainer(): HTMLElement {
  // create container & reference
  const modalRef = useRef();

  if (modalRef.current == null) {
    modalRef.current = createModalContainer();
  }

  const modalWrapper = modalRef.current;

  // add/remove modal wrapper
  useLayoutEffect(() => {
    const body = document.body;
    let currentBodyScrollPosition;

    // add wrapper to document
    // and lock scrolling
    if (body != null) {
      body.appendChild(modalWrapper);
      currentBodyScrollPosition = lockBodyScroll();
    }

    // remove wrapper
    // and unlock scrolling when component will be removed
    return () => {
      if (body != null) {
        body.removeChild(modalWrapper);
        unlockBodyScroll(currentBodyScrollPosition);
      }
    };
  }, [modalWrapper]);

  return modalWrapper;
}

/**
 * Render passed children in a modal view.
 *
 * @param {ModalProps} props - component properties
 *
 * @returns {React$Portal}
 */
export const Modal = ({ className, onClose, children }: ModalProps) => {
  const modalWrapper = useModalContainer();

  return ReactDOM.createPortal(
    <React.Fragment>
      <div className={styles.modal} onClick={onClose} />
      <div className={`${styles.modalContent} ${className}`}>{children}</div>
    </React.Fragment>,
    modalWrapper,
  );
};
